import { supabase } from '../lib/supabase'
import { kaseyaAPI } from './kaseya-api'
import type { SendDeviceAgentParams } from '../types/supabase'

export interface Device {
  id: string
  company_id: string
  user_id?: string
  name: string
  os_type?: string
  os_version?: string
  status: 'online' | 'offline' | 'warning' | 'error'
  last_seen?: string
  agent_version?: string
  external_id?: string
  metadata?: Record<string, any>
  created_at: string
  updated_at: string
  // New detailed fields
  operating_system?: string
  ip_address?: string
  uptime?: string
  last_seen_display?: string
  notifications_count?: number
  cpu_usage?: number
  memory_usage?: number
  storage_usage?: number
  logged_in_user?: string
  has_full_data?: boolean
  // Additional UI fields
  device_id_display?: string
  user_email?: string
  user_full_name?: string
  ticket_count?: number
  last_active?: string
  device_type_display?: string
  organization_name?: string
  // Kaseya asset fields
  mac_address?: string
  computer_domain?: string
  antivirus_status?: string
  antivirus_definitions_status?: string
  firewall_enabled?: boolean
  uac_enabled?: boolean
  ransomware_detection_status?: string
  critical_updates_count?: number
  important_updates_count?: number
  moderate_updates_count?: number
  low_updates_count?: number
  total_disk_space_gb?: number
  free_disk_space_gb?: number
  disk_usage_percent?: number
  memory_total_gb?: number
  memory_used_gb?: number
  installed_software_count?: number
  installed_software?: Array<{ Name: string; Version: string; Publisher: string }>
  asset_last_synced_at?: string
}

export interface CreateDeviceData {
  name: string
  os_type?: string
  os_version?: string
  device_type?: string
  mac_address?: string
  user_id?: string
  kaseya_group_id?: number
  deployment_email?: string
  deployment_os?: string
  deployment_arch?: string
}

export const devicesService = {
  // Get all devices for a company (or all companies for admins)
  async getDevices(companyId?: string): Promise<Device[]> {
    let query = supabase
      .from('devices')
      .select(`
        *,
        companies!inner(name),
        users(email, full_name)
      `)
      .order('created_at', { ascending: false })

    if (companyId) {
      query = query.eq('company_id', companyId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching devices:', error)
      throw error
    }

    // Map the joined data to flat structure
    const devices = (data || []).map((device: any) => ({
      ...device,
      organization_name: device.companies?.name,
      user_email: device.users?.email,
      user_full_name: device.users?.full_name,
      companies: undefined,
      users: undefined
    }))

    return devices
  },

  // Get a single device by ID
  async getDevice(deviceId: string): Promise<Device | null> {
    const { data, error } = await supabase
      .from('devices')
      .select(`
        *,
        companies!inner(name),
        users(email, full_name)
      `)
      .eq('id', deviceId)
      .single()

    if (error) {
      console.error('Error fetching device:', error)
      throw error
    }

    // Map the joined data to flat structure
    if (data) {
      return {
        ...data,
        organization_name: data.companies?.name,
        user_email: data.users?.email,
        user_full_name: data.users?.full_name,
        companies: undefined,
        users: undefined
      }
    }

    return null
  },

  // Create a new device
  async createDevice(
    deviceData: CreateDeviceData,
    companyId: string,
    sendAgentNow: boolean = false
  ): Promise<Device> {
    const { data, error } = await supabase
      .from('devices')
      .insert({
        company_id: companyId,
        name: deviceData.name,
        os_type: deviceData.os_type,
        os_version: deviceData.os_version,
        user_id: deviceData.user_id,
        kaseya_group_id: deviceData.kaseya_group_id,
        deployment_email: deviceData.deployment_email,
        status: 'offline', // Default status for new devices
        metadata: {
          device_type: deviceData.device_type,
          mac_address: deviceData.mac_address
        }
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating device:', error)
      throw error
    }

    // If email provided and sendAgentNow, trigger agent deployment
    if (deviceData.deployment_email && sendAgentNow && deviceData.kaseya_group_id) {
      try {
        await sendDeviceAgent({
          deviceId: data.id,
          email: deviceData.deployment_email,
          osType: deviceData.deployment_os || 'windows',
          architecture: deviceData.deployment_arch || 'x64',
          kaseyaGroupId: deviceData.kaseya_group_id,
          companyId
        })
        console.log(`✅ Device agent email sent to ${deviceData.deployment_email}`)
      } catch (emailError) {
        console.error('Failed to send device agent email:', emailError)
        // Don't throw - device was created successfully
      }
    }

    return data
  },

  // Update device details
  async updateDevice(deviceId: string, updates: Partial<Device>): Promise<Device> {
    const { data, error } = await supabase
      .from('devices')
      .update(updates)
      .eq('id', deviceId)
      .select()
      .single()

    if (error) {
      console.error('Error updating device:', error)
      throw error
    }

    return data
  },

  // Delete a device
  async deleteDevice(deviceId: string): Promise<void> {
    const { error } = await supabase
      .from('devices')
      .delete()
      .eq('id', deviceId)

    if (error) {
      console.error('Error deleting device:', error)
      throw error
    }
  }
}

/**
 * Send device agent installer email
 * Creates deployment link and triggers email via edge function
 */
export async function sendDeviceAgent(params: SendDeviceAgentParams): Promise<any> {
  const { deviceId, email, osType, architecture, kaseyaGroupId, companyId } = params

  console.log(`Sending device agent for ${deviceId} to ${email}`)

  // Generate unique access token
  const accessToken = crypto.randomUUID().replace(/-/g, '').substring(0, 32)

  // Get installer URL based on OS and architecture
  let installerUrl: string
  try {
    installerUrl = await kaseyaAPI.getGroupPackageWithArch(
      kaseyaGroupId,
      osType,
      architecture
    )
  } catch (error) {
    console.error('Failed to get installer URL:', error)
    throw new Error('Failed to generate installer URL')
  }

  // Create deployment link record
  const { data: deploymentLink, error: deploymentError } = await supabase
    .from('device_deployment_links')
    .insert({
      device_id: deviceId,
      company_id: companyId,
      download_url: installerUrl,
      deployment_type: osType,
      os_architecture: architecture,
      access_token: accessToken,
      status: 'pending',
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    })
    .select()
    .single()

  if (deploymentError) {
    console.error('Failed to create deployment link:', deploymentError)
    throw new Error('Failed to create deployment link')
  }

  // Update device with deployment_link_id
  await supabase
    .from('devices')
    .update({ deployment_link_id: deploymentLink.id })
    .eq('id', deviceId)

  // Call edge function to send email
  const { error: emailError } = await supabase.functions.invoke(
    'send-device-agent',
    {
      body: {
        deploymentLinkId: deploymentLink.id,
        email: email,
        accessToken: accessToken
      }
    }
  )

  if (emailError) {
    console.error('Failed to send device agent email:', emailError)
    throw new Error('Failed to send agent email')
  }

  console.log(`✅ Device agent email sent successfully`)
  return deploymentLink
}
